import { useT } from '@/i18n';

/** The ORCA ecosystem around the logo (CLAUDE.md §38–39): the original logo at
 *  the centre, thin lines out to the product's functions, slow light pulses
 *  along them; a function lights up on hover and the rest dim. Positions are a
 *  share of the square stage, so labels stay crisp HTML text in every language.
 *  Pure CSS/SVG; all motion stops with «reduce motion». */
const NODES = [
  { key: 'coins', x: 50, y: 9 },
  { key: 'alerts', x: 82, y: 22 },
  { key: 'whales', x: 92, y: 52 },
  { key: 'calendar', x: 79, y: 82 },
  { key: 'ai', x: 50, y: 93 },
  { key: 'portfolio', x: 20, y: 81 },
  { key: 'social', x: 8, y: 51 },
  { key: 'wallstreet', x: 19, y: 21 },
] as const;

const C = 300; // centre of the 600×600 stage
const START = 104; // lines begin just outside the logo

function ray(x: number, y: number) {
  const tx = x * 6, ty = y * 6, dx = tx - C, dy = ty - C, len = Math.hypot(dx, dy);
  const sx = C + (dx / len) * START, sy = C + (dy / len) * START;
  const ex = tx - (dx / len) * 10, ey = ty - (dy / len) * 10;
  return { d: `M${sx.toFixed(1)} ${sy.toFixed(1)} L${ex.toFixed(1)} ${ey.toFixed(1)}`, len: Math.hypot(ex - sx, ey - sy) };
}

export function AuthVisual() {
  const { t } = useT('auth');
  return (
    <aside className="auth-visual" aria-label={t('visual.label')}>
      <div className="av-sky" aria-hidden="true" />
      <div className="av-brand" aria-hidden="true"><img src="/assets/brand/orca-mark.png" alt="" width={360} height={360} /><b>orca</b></div>
      <div className="av-stage">
        <svg className="av-net" viewBox="0 0 600 600" aria-hidden="true">
          <defs>
            <radialGradient id="avFade" cx="300" cy="300" r="300" gradientUnits="userSpaceOnUse">
              <stop offset=".3" stopColor="#b9d0ff" stopOpacity=".42" />
              <stop offset="1" stopColor="#b9d0ff" stopOpacity=".08" />
            </radialGradient>
          </defs>
          <ellipse className="av-orbit" cx="300" cy="300" rx="232" ry="238" />
          {NODES.map((n, i) => {
            const r = ray(n.x, n.y);
            return (
              <g key={n.key} className={`av-ray ray-${n.key}`} style={{ ['--i' as string]: i, ['--len' as string]: r.len.toFixed(0) }}>
                <path className="av-line" d={r.d} />
                <path className="av-pulse" d={r.d} />
              </g>
            );
          })}
        </svg>
        <div className="av-halo" aria-hidden="true" />
        <img className="av-logo" src="/assets/brand/orca-logo.png" alt={t('visual.logoAlt')} width={445} height={464} decoding="async" />
        <ul className="av-nodes" aria-hidden="true">
          {NODES.map((n, i) => (
            <li key={n.key} className={`av-node ${n.x > 60 ? 'r' : n.x < 40 ? 'l' : n.y < 50 ? 't' : 'b'}`} data-node={n.key}
              style={{ left: `${n.x}%`, top: `${n.y}%`, ['--i' as string]: i }}>
              <i /><span>{t(`visual.nodes.${n.key}`)}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="av-copy">
        <span className="av-eyebrow">{t('visual.eyebrow')}</span>
        <p className="av-slogan">{t('visual.slogan')}</p>
        <p className="av-domains">{t('visual.domains')}</p>
      </div>
    </aside>
  );
}
